import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArtistPortalConnectFailure,
  ArtistPortalConnectResult,
  artistPortalConnectReturnParams,
  artistPortalServices,
} from 'fym-shared/src/smartlinks/artistPortals';

export interface PortalConnectOutcome {
  integrationType: string;
  result: ArtistPortalConnectResult;
  /** Set only when `result` is `error`. */
  reason?: ArtistPortalConnectFailure;
}

function toResult(value: string | null): ArtistPortalConnectResult | undefined {
  return Object.values(ArtistPortalConnectResult).find((one) => one === value);
}

function toReason(value: string | null): ArtistPortalConnectFailure {
  return Object.values(ArtistPortalConnectFailure).find((one) => one === value) ?? ArtistPortalConnectFailure.unknown;
}

/** Reads an outcome off the query. Undefined when the query holds none, or one we do not know. */
export function readPortalConnectOutcome(searchParams: URLSearchParams): PortalConnectOutcome | undefined {
  const integrationType = searchParams.get(artistPortalConnectReturnParams.service);
  const result = toResult(searchParams.get(artistPortalConnectReturnParams.result));
  if (integrationType === null || !artistPortalServices.includes(integrationType) || result === undefined) {
    return undefined;
  }
  if (result !== ArtistPortalConnectResult.error) {
    return { integrationType, result };
  }
  return { integrationType, result, reason: toReason(searchParams.get(artistPortalConnectReturnParams.reason)) };
}

interface UsePortalConnectReturnProps {
  /** Called once, after a sign-in came back, so the cards show the new state. */
  onReturn: () => void;
}

/**
 * What music24-api said when it sent the browser back from a YouTube sign-in.
 *
 * The answer is in the query of the Catalog screen. The hook reads it once and
 * removes it from the address, so a reload does not show the same result again
 * and a copied link holds no old result.
 */
export function usePortalConnectReturn({ onReturn }: UsePortalConnectReturnProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [outcome, setOutcome] = useState<PortalConnectOutcome>();
  const isRead = useRef(false);

  useEffect(() => {
    if (isRead.current) {
      return;
    }
    const read = readPortalConnectOutcome(searchParams);
    if (read === undefined) {
      return;
    }
    isRead.current = true;
    setOutcome(read);

    const next = new URLSearchParams(searchParams);
    Object.values(artistPortalConnectReturnParams).forEach((name) => next.delete(name));
    setSearchParams(next, { replace: true });
    onReturn();
  }, [onReturn, searchParams, setSearchParams]);

  const dismiss = useCallback(() => setOutcome(undefined), []);

  return { outcome, dismiss };
}
